import React, { useState, useEffect, useRef, useCallback } from 'react';

// Game constants
const GRID_SIZE = 20;
const CELL_SIZE = 15;
const CANVAS_SIZE = GRID_SIZE * CELL_SIZE;
const GAME_SPEED = 150;

const COLORS = {
  background: '#1a1207',
  grid: '#2d1f0d',
  snakeHead: '#c2a366',
  snakeBody: '#8b7355',
  food: '#6b8e23'
};

const DIRECTIONS = {
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  w: { x: 0, y: -1 },
  s: { x: 0, y: 1 },
  a: { x: -1, y: 0 },
  d: { x: 1, y: 0 }
};

const randomPosition = () => ({
  x: Math.floor(Math.random() * GRID_SIZE),
  y: Math.floor(Math.random() * GRID_SIZE)
});

const SnakeGame = () => {
  const canvasRef = useRef(null);
  const gameRef = useRef(null);
  const lastUpdateRef = useRef(0);

  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);

  const initGame = useCallback(() => ({
    snake: [{ x: 10, y: 10 }],
    food: randomPosition(),
    direction: { x: 1, y: 0 },
    nextDirection: { x: 1, y: 0 }
  }), []);

  const resetGame = useCallback(() => {
    gameRef.current = initGame();
    setScore(0);
    setGameOver(false);
    setGameStarted(true);
  }, [initGame]);

  // Game loop
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    gameRef.current = gameRef.current || initGame();

    const drawGrid = () => {
      ctx.strokeStyle = COLORS.grid;
      for (let i = 0; i <= GRID_SIZE; i++) {
        ctx.beginPath();
        ctx.moveTo(i * CELL_SIZE, 0);
        ctx.lineTo(i * CELL_SIZE, CANVAS_SIZE);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(0, i * CELL_SIZE);
        ctx.lineTo(CANVAS_SIZE, i * CELL_SIZE);
        ctx.stroke();
      }
    };

    const drawSnake = (snake) => {
      snake.forEach((seg, i) => {
        ctx.fillStyle = i === 0 ? COLORS.snakeHead : COLORS.snakeBody;
        ctx.fillRect(
          seg.x * CELL_SIZE + 1,
          seg.y * CELL_SIZE + 1,
          CELL_SIZE - 2,
          CELL_SIZE - 2
        );
      });
    };

    const drawFood = (food) => {
      ctx.fillStyle = COLORS.food;
      ctx.beginPath();
      ctx.arc(
        food.x * CELL_SIZE + CELL_SIZE / 2,
        food.y * CELL_SIZE + CELL_SIZE / 2,
        CELL_SIZE / 2 - 2,
        0,
        Math.PI * 2
      );
      ctx.fill();
    };

    const update = () => {
      const game = gameRef.current;
      game.direction = game.nextDirection;

      const newHead = {
        x: (game.snake[0].x + game.direction.x + GRID_SIZE) % GRID_SIZE,
        y: (game.snake[0].y + game.direction.y + GRID_SIZE) % GRID_SIZE
      };

      // Collision detection
      if (game.snake.some(seg => seg.x === newHead.x && seg.y === newHead.y)) {
        setGameOver(true);
        return false;
      }

      game.snake.unshift(newHead);

      // Food collision
      if (newHead.x === game.food.x && newHead.y === game.food.y) {
        setScore(s => s + 10);
        game.food = randomPosition();
      } else {
        game.snake.pop();
      }

      return true;
    };

    const render = () => {
      ctx.fillStyle = COLORS.background;
      ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      drawGrid();
      drawSnake(gameRef.current.snake);
      drawFood(gameRef.current.food);
    };

    let animationId;
    const gameLoop = (timestamp) => {
      if (timestamp - lastUpdateRef.current >= GAME_SPEED) {
        if (!update()) return;
        lastUpdateRef.current = timestamp;
      }
      render();
      animationId = requestAnimationFrame(gameLoop);
    };

    animationId = requestAnimationFrame(gameLoop);

    // Keyboard controls
    const handleKeyDown = (e) => {
      const newDir = DIRECTIONS[e.key];
      const current = gameRef.current.direction;
      if (newDir && (newDir.x !== -current.x || newDir.y !== -current.y)) {
        gameRef.current.nextDirection = newDir;
      }
    };

    // Touch controls for mobile
    let touchStartX = 0;
    let touchStartY = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const dx = touchEndX - touchStartX;
      const dy = touchEndY - touchStartY;

      // Minimum swipe distance
      if (Math.abs(dx) < 30 && Math.abs(dy) < 30) return;

      const current = gameRef.current.direction;
      let newDir;

      if (Math.abs(dx) > Math.abs(dy)) {
        // Horizontal swipe
        newDir = dx > 0 ? { x: 1, y: 0 } : { x: -1, y: 0 };
      } else {
        // Vertical swipe
        newDir = dy > 0 ? { x: 0, y: 1 } : { x: 0, y: -1 };
      }

      if (newDir && (newDir.x !== -current.x || newDir.y !== -current.y)) {
        gameRef.current.nextDirection = newDir;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    canvas.addEventListener('touchstart', handleTouchStart, { passive: true });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('keydown', handleKeyDown);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [gameStarted, gameOver, initGame]);

  const isMobile = window.innerWidth <= 768;

  const renderOverlay = () => {
    if (!gameStarted) {
      return (
        <div className="game-overlay">
          <button onClick={resetGame}>START GAME</button>
          <p>{isMobile ? 'Swipe to move' : 'Use WASD or Arrow Keys'}</p>
        </div>
      );
    }

    if (gameOver) {
      return (
        <div className="game-overlay">
          <p>GAME OVER</p>
          <p>Final Score: {score}</p>
          <button onClick={resetGame}>PLAY AGAIN</button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="snake-game">
      <div className="game-header">
        <span>SNAKE.exe</span>
        <span>Score: {score}</span>
      </div>
      <canvas ref={canvasRef} width={CANVAS_SIZE} height={CANVAS_SIZE} />
      {renderOverlay()}
    </div>
  );
};

export default SnakeGame;
